import React from 'react';
import { Turnstile, TurnstileInstance } from '@marsidev/react-turnstile';
import { APP_CONFIG } from '../utils/constants';

interface InputSectionProps {
  value: string;
  onChange: (value: string) => void;
  onTransform: () => void;
  isLoading: boolean;
  error: string | null;
  turnstileToken: string;
  onTurnstileToken: (token: string) => void;
  turnstileRef: React.RefObject<TurnstileInstance | undefined>;
}

const TURNSTILE_SITEKEY = process.env.REACT_APP_TURNSTILE_SITEKEY || '';

const InputSection: React.FC<InputSectionProps> = ({
  value,
  onChange,
  onTransform,
  isLoading,
  error,
  turnstileToken,
  onTurnstileToken,
  turnstileRef,
}) => {
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onTransform();
  };

  const isDisabled =
    isLoading ||
    !value.trim() ||
    value.length > APP_CONFIG.MAX_INPUT_LENGTH ||
    !turnstileToken;

  return (
    <form onSubmit={handleSubmit} className="space-y-3">
      <div className="relative">
        <textarea
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder="Enter your text here to transform it into eloquent English..."
          className={`w-full min-h-20 p-3 text-base border-4 border-neo-black bg-neo-white font-medium resize-y focus:outline-none focus:shadow-neo transition-all duration-200 ${
            error ? 'border-red-500 bg-red-50' : ''
          }`}
          maxLength={APP_CONFIG.MAX_INPUT_LENGTH}
          rows={3}
        />
        <div className="absolute bottom-2 right-2 text-sm text-gray-600 font-medium">
          {value.length}/{APP_CONFIG.MAX_INPUT_LENGTH}
        </div>
      </div>

      {TURNSTILE_SITEKEY ? (
        <Turnstile
          ref={turnstileRef}
          siteKey={TURNSTILE_SITEKEY}
          onSuccess={onTurnstileToken}
          onExpire={() => onTurnstileToken('')}
          onError={() => onTurnstileToken('')}
        />
      ) : (
        <div className="bg-red-50 border-3 border-red-500 p-3 text-red-700 font-medium">
          Turnstile sitekey is not configured. Set REACT_APP_TURNSTILE_SITEKEY.
        </div>
      )}

      <div className="flex items-center justify-between">
        <button
          type="submit"
          disabled={isDisabled}
          className={`px-6 py-2 text-base font-bold border-4 border-neo-black transition-all duration-200 ${
            isDisabled
              ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
              : 'bg-neo-yellow text-neo-black hover:shadow-neo active:shadow-none active:translate-x-1 active:translate-y-1'
          }`}
        >
          {isLoading ? 'Transforming...' : 'Transform'}
        </button>

        {value.length > APP_CONFIG.MAX_INPUT_LENGTH && (
          <p className="text-red-600 font-medium">
            Text exceeds maximum length of {APP_CONFIG.MAX_INPUT_LENGTH} characters
          </p>
        )}
      </div>
    </form>
  );
};

export default InputSection;
